import * as React from 'react';

import {
  CurrencyCode,
  enumCurrencyCode,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { humanReadableShortPricingUnit } from '@rematter/ui-web';

import { PackageFormatCell } from './fields';
import { QuoteLineItemData, QuoteLineItemUpdate } from './types';

import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import {
  ComputedPriceCell,
  DeleteButtonCell,
  HeaderCell,
  InventoryItemSelect,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
  NotesFieldCell,
  PriceFieldCell,
  QuantityFieldCell,
  UnitFieldCell,
} from '~/shared/components/line-items';

const QUOTE_LINE_ITEM_GRID =
  'grid-cols-[minmax(12rem,_2fr)_1fr_1fr_100px_1fr_1fr_var(--size-input)_var(--size-input)]';

interface QuoteRowProps {
  lineItem: QuoteLineItemData;
  disabled: boolean;
  onUpdateLineItem: (id: string, updates: QuoteLineItemUpdate) => void;
  onDeleteLineItem: (id: string) => void;
}

const QuoteRow = React.memo<QuoteRowProps>(
  ({ lineItem, disabled, onUpdateLineItem, onDeleteLineItem }) => {
    const unitSuffix = humanReadableShortPricingUnit(lineItem.unit);

    const handleMaterialChange = React.useCallback(
      (itemId?: string) =>
        onUpdateLineItem(lineItem.id, { itemId: itemId ?? null }),
      [lineItem.id, onUpdateLineItem],
    );

    const handlePackageFormatChange = React.useCallback(
      (packageFormat?: string) =>
        onUpdateLineItem(lineItem.id, {
          packageFormat: packageFormat ?? null,
        }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleUnitChange = React.useCallback(
      (unit?: Unit) => {
        if (unit) onUpdateLineItem(lineItem.id, { unit });
      },
      [lineItem.id, onUpdateLineItem],
    );

    const handlePriceChange = React.useCallback(
      (amount: number) =>
        onUpdateLineItem(lineItem.id, {
          unitPrice: {
            amount,
            currencyCode:
              (lineItem.unitPrice?.currencyCode as CurrencyCode) ??
              enumCurrencyCode.USD,
          },
        }),
      [lineItem.id, lineItem.unitPrice?.currencyCode, onUpdateLineItem],
    );

    const handleQuantityChange = React.useCallback(
      (quantity: number) => onUpdateLineItem(lineItem.id, { quantity }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleNotesChange = React.useCallback(
      (notes: string) => onUpdateLineItem(lineItem.id, { notes }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleDelete = React.useCallback(
      () => onDeleteLineItem(lineItem.id),
      [lineItem.id, onDeleteLineItem],
    );

    return (
      <LineItemRow gridTemplate={QUOTE_LINE_ITEM_GRID}>
        <InventoryItemSelect
          value={lineItem.itemId}
          onChange={handleMaterialChange}
          disabled={disabled}
        />
        <PackageFormatCell
          value={lineItem.packageFormat}
          onChange={handlePackageFormatChange}
          disabled={disabled}
        />
        <PriceFieldCell
          value={lineItem.unitPrice?.amount ?? 0}
          onChange={handlePriceChange}
          disabled={disabled}
          suffix={unitSuffix ? `/${unitSuffix}` : undefined}
        />
        <UnitFieldCell
          value={lineItem.unit}
          onChange={handleUnitChange}
          disabled={disabled}
        />
        <QuantityFieldCell
          value={lineItem.quantity}
          pricingUnit={lineItem.unit as Unit}
          onChange={handleQuantityChange}
          disabled={disabled}
        />
        <ComputedPriceCell value={lineItem.totalPrice} />
        <NotesFieldCell
          value={lineItem.notes}
          onChange={handleNotesChange}
          disabled={disabled}
        />
        <DeleteButtonCell onDelete={handleDelete} disabled={disabled} />
      </LineItemRow>
    );
  },
);

interface QuoteLineItemsTableProps {
  lineItems: QuoteLineItemData[];
  disabled?: boolean;
  onUpdateLineItem: (id: string, updates: QuoteLineItemUpdate) => void;
  onAddLineItem: () => void;
  onDeleteLineItem: (id: string) => void;
}

export const QuoteLineItemsTable: React.FC<QuoteLineItemsTableProps> = ({
  lineItems,
  disabled = false,
  onUpdateLineItem,
  onAddLineItem,
  onDeleteLineItem,
}) => {
  const t = useTranslations();
  const isEmpty = lineItems.length === 0;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={disabled}
      disabled={disabled}
      addButtonLabel={t.quotes.lineItems.addButton}
      emptyTitle={t.quotes.lineItems.empty}
      emptyDescription={t.quotes.lineItems.emptyDescription}
      className="@QuoteLineItemsTable"
    >
      <LineItemHeader gridTemplate={QUOTE_LINE_ITEM_GRID}>
        <HeaderCell>{t.quotes.lineItems.fields.item}</HeaderCell>
        <HeaderCell>{t.quotes.lineItems.fields.packageFormat}</HeaderCell>
        <HeaderCell>{t.quotes.lineItems.fields.unitPrice}</HeaderCell>
        <HeaderCell>{t.quotes.lineItems.fields.unit}</HeaderCell>
        <HeaderCell>{t.quotes.lineItems.fields.quantity}</HeaderCell>
        <HeaderCell>{t.quotes.lineItems.fields.total}</HeaderCell>
        <HeaderCell>{t.common.labels.notes}</HeaderCell>
        <HeaderCell />
      </LineItemHeader>

      <div className="flex flex-col gap-2">
        {lineItems.map((lineItem) => (
          <QuoteRow
            key={lineItem.id}
            lineItem={lineItem}
            disabled={disabled}
            onUpdateLineItem={onUpdateLineItem}
            onDeleteLineItem={onDeleteLineItem}
          />
        ))}
      </div>
    </LineItemTable>
  );
};
